import { formatLocalDateTime } from '../../utils/localDateTime';
import React, { useEffect, useState } from 'react';
import { ClipboardDocumentIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { HelpdeskTicket, Organization, UserProfile } from '@types';
import { confirmApp } from '../../common/appDialog';
import { getOrganizationDisplayName } from '../../common/organizationDisplayName';

const SupportQueuePanel: React.FC<{ currentUser: UserProfile; organizationId?: string; enabled?: boolean; onTicketsChanged?: (tickets: HelpdeskTicket[]) => void }> = ({ currentUser, organizationId, enabled = true, onTicketsChanged }) => {
    const [tickets, setTickets] = useState<HelpdeskTicket[]>([]);
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [orgs, setOrgs] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(true);
    const [ticketFilter, setTicketFilter] = useState<'all' | HelpdeskTicket['status']>('all');
    const [ticketReplyDrafts, setTicketReplyDrafts] = useState<Record<string, string>>({});
    const [ticketResolving, setTicketResolving] = useState<string | null>(null);
    const [triageRetrying, setTriageRetrying] = useState<string | null>(null);
    const [message, setMessage] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const showToast = (msg: string, type: 'success' | 'error' = 'success') => setMessage({ msg, type });

    useEffect(() => {
        if (!enabled)
            return;
        let active = true;
        setLoading(true);
        setMessage(null);
        setTickets([]);
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
        void Promise.all([
            callable({ action: 'listSupportTickets', data: organizationId ? { organizationId } : {} }),
            callable({ action: 'listDirectory', data: {} }),
        ]).then(([ticketResponse, directoryResponse]) => {
            if (!active)
                return;
            const support = ticketResponse.data as { tickets: HelpdeskTicket[] };
            const directory = directoryResponse.data as { users: UserProfile[]; organizations: Organization[] };
            setTickets(support.tickets);
            setUsers(directory.users);
            setOrgs(directory.organizations);
        }).catch(error => {
            if (active)
                showToast(error.message || 'Support queue could not be loaded.', 'error');
        }).finally(() => {
            if (active)
                setLoading(false);
        });
        return () => { active = false; };
    }, [organizationId, currentUser.uid, currentUser.adminRole, enabled]);

    useEffect(() => {
        onTicketsChanged?.(tickets);
    }, [tickets, onTicketsChanged]);

    // ── Ticket actions ──
    const handleTicketStatus = async (id: string, status: HelpdeskTicket['status']) => {
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            await callable({ action: 'updateSupportTicket', data: { ticketId: id, status } });
            setTickets(prev => prev.map(t => t.id === id ? { ...t, status } : t));
        } catch (error: any) {
            showToast(error?.message || 'Failed updating ticket status.', 'error');
        }
    };
    const handleResolveTicket = async (id: string) => {
        const consultantReply = (ticketReplyDrafts[id] || '').trim();
        if (!consultantReply) {
            showToast('Write a reply before resolving.', 'error');
            return;
        }
        setTicketResolving(id);
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            await callable({ action: 'updateSupportTicket', data: { ticketId: id, status: 'resolved', consultantReply } });
            const resolvedAt = new Date().toISOString();
            setTickets(prev => prev.map(ticket => ticket.id === id ? { ...ticket, status: 'resolved', consultantReply, resolvedAt, updatedAt: resolvedAt } : ticket));
            setTicketReplyDrafts(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            showToast('Ticket resolved. Submitter update posted.');
        } catch (error: any) {
            showToast(error?.message || 'Failed resolving ticket.', 'error');
        } finally {
            setTicketResolving(null);
        }
    };
    const handleDeleteTicket = async (id: string) => {
        if (!await confirmApp('Delete this ticket permanently?'))
            return;
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            await callable({ action: 'deleteSupportTicket', data: { ticketId: id } });
            setTickets(prev => prev.filter(t => t.id !== id));
            showToast('Ticket deleted.');
        } catch (error: any) {
            showToast(error?.message || 'Failed deleting ticket.', 'error');
        }
    };

    const handleTicketResolution = async (id: string, resolutionDetails: string) => {
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
        await callable({ action: 'updateSupportTicket', data: { ticketId: id, resolutionDetails } });
    };

    const handleRetryTriage = async (ticket: HelpdeskTicket) => {
        if (!ticket.id)
            return;
        setTriageRetrying(ticket.id);
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'inviteTeamMember');
            const response = await callable({ action: 'retryHelpdeskTriage', data: { ticketId: ticket.id } });
            const result = response.data as { success: boolean; aiTriage: HelpdeskTicket['aiTriage'] };
            setTickets(previous => previous.map(candidate => candidate.id === ticket.id ? { ...candidate, aiTriage: result.aiTriage } : candidate));
            showToast(result.success ? 'AI triage completed.' : result.aiTriage?.processingNote || 'AI triage failed.', result.success ? 'success' : 'error');
        } catch (error: any) {
            showToast(error?.message || 'AI triage retry failed.', 'error');
        } finally {
            setTriageRetrying(null);
        }
    };

    const filteredTickets = tickets.filter(ticket => ticketFilter === 'all' || ticket.status === ticketFilter);
    const organizationName = (id?: string) => {
        const organization = orgs.find(candidate => candidate.id === id);
        return organization ? getOrganizationDisplayName(organization) : id || '—';
    };
    const ticketUserName = (ticket: HelpdeskTicket) => {
        const submitter = users.find(user => user.uid === ticket.userId || user.email === ticket.userEmail);
        return submitter?.displayName || ticket.userName || ticket.userEmail || ticket.userId;
    };

    return <section aria-label="Support Queue" className="space-y-4">
        <h2 className="text-xl font-extrabold text-slate-900">Support Queue</h2>
        {message && <p role={message.type === 'error' ? 'alert' : 'status'} className={message.type === 'error' ? 'rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700' : 'rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700'}>{message.msg}</p>}
        {loading ? <p role="status" className="p-10 text-center text-sm text-slate-500">Loading support queue…</p> : (
            <div className="space-y-4 animate-fade-in">
                <div className="flex items-center justify-between">
                    <div className="flex gap-2 flex-wrap">
                        {(['all', 'open', 'in-progress', 'resolved', 'closed'] as const).map(s => (
                            <button
                                key={s}
                                onClick={() => setTicketFilter(s)}
                                className={`px-3 py-1 rounded-full text-xs font-bold capitalize transition-all ${
                                    ticketFilter === s ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                {s.replace('-', ' ')}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-slate-400 font-medium">{filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                    {filteredTickets.length === 0 ? (
                        <p className="p-12 text-center text-slate-400 italic">No tickets found.</p>
                    ) : (
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200">
                                <tr>
                                    <th className="px-5 py-3">Ticket</th>
                                    <th className="px-5 py-3">User / Org</th>
                                    <th className="px-5 py-3">Priority</th>
                                    <th className="px-5 py-3">Status</th>
                                    <th className="px-5 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredTickets.map(t => (
                                    <React.Fragment key={t.id}>
                                        <tr className="hover:bg-slate-50 transition-colors">
                                            <td className="px-5 py-4 max-w-sm">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                                                        {t.category.replace(' and ', ' & ')}
                                                    </span>
                                                    <span className="text-[10px] text-slate-400 font-mono">#{t.id?.slice(-6)}</span>
                                                    {/* AI Triage Badge */}
                                                    {t.aiTriage && (
                                                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border flex items-center gap-1 ${
                                                            t.aiTriage.category === 'bug' ? 'bg-red-50 text-red-600 border-red-200' :
                                                            t.aiTriage.category === 'billing' ? 'bg-amber-50 text-amber-600 border-amber-200' :
                                                            t.aiTriage.category === 'account' ? 'bg-violet-50 text-violet-600 border-violet-200' :
                                                            t.aiTriage.category === 'feature_request' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                                                            t.aiTriage.category === 'training' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                                            'bg-slate-50 text-slate-500 border-slate-200'
                                                        }`}>
                                                            🤖 {t.aiTriage.category.replace('_', ' ')}
                                                            <span className={`w-1.5 h-1.5 rounded-full ${
                                                                t.aiTriage.confidence === 'high' ? 'bg-emerald-500' :
                                                                t.aiTriage.confidence === 'medium' ? 'bg-amber-500' : 'bg-red-400'
                                                            }`} title={`AI confidence: ${t.aiTriage.confidence}`} />
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="font-semibold text-slate-800 line-clamp-1">{t.subject}</p>
                                                <p className="text-xs text-slate-400 line-clamp-2 mt-0.5">{t.description}</p>
                                                <p className="text-[10px] text-slate-400 mt-1">{formatLocalDateTime(t.createdAt)}</p>
                                            </td>
                                            <td className="px-5 py-4">
                                                <p className="text-sm font-bold text-slate-700">{ticketUserName(t)}</p>
                                                <a href={`mailto:${t.userEmail}`} className="text-xs text-blue-600 hover:underline">{t.userEmail}</a>
                                                <p className="text-xs font-semibold text-slate-500 truncate max-w-[180px]">{organizationName(t.organizationId)}</p>
                                            </td>
                                            <td className="px-5 py-4"><span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-bold uppercase text-slate-600">{t.priority}</span></td>
                                            <td className="px-5 py-4">
                                                <select
                                                    value={t.status}
                                                    onChange={e => e.target.value === 'resolved'
                                                        ? void handleResolveTicket(t.id!)
                                                        : void handleTicketStatus(t.id!, e.target.value as HelpdeskTicket['status'])}
                                                    className="text-xs font-bold rounded-lg p-1.5 border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                                >
                                                    <option value="open">Open</option>
                                                    <option value="in-progress">In Progress</option>
                                                    <option value="resolved">Resolved</option>
                                                    <option value="closed">Closed</option>
                                                </select>
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <button
                                                    onClick={() => handleDeleteTicket(t.id!)}
                                                    className="text-slate-400 hover:text-red-600 transition-colors"
                                                    title="Delete Ticket"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                        {/* Resolution / AI Triage Panel */}
                                        {(t.status === 'open' || t.status === 'in-progress') && (
                                            <tr className="bg-slate-50/50">
                                                <td colSpan={5} className="px-5 py-4 border-b border-slate-200">
                                                    {/* AI Triage Info Bar */}
                                                    {t.aiTriage && (
                                                        <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start gap-3">
                                                            <span className="text-indigo-500 text-lg flex-shrink-0">🤖</span>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-0.5">
                                                                    AI Triage — {t.aiTriage.category.replace('_', ' ')} · Confidence: {t.aiTriage.confidence}
                                                                </p>
                                                                <p className="text-xs text-indigo-700 italic">{t.aiTriage.processingNote}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => handleRetryTriage(t)}
                                                                disabled={triageRetrying === t.id}
                                                                className="flex-shrink-0 text-[10px] font-bold px-2.5 py-1.5 bg-white text-indigo-700 border border-indigo-300 rounded-lg hover:bg-indigo-100 disabled:opacity-50 uppercase tracking-wider"
                                                            >
                                                                {triageRetrying === t.id ? 'Testing...' : 'Retry AI Triage'}
                                                            </button>
                                                            {t.aiTriage.suggestedResponse && (
                                                                <button
                                                                    onClick={() => navigator.clipboard.writeText(t.aiTriage!.suggestedResponse)}
                                                                    className="flex-shrink-0 text-[10px] font-bold px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1 uppercase tracking-wider"
                                                                    title="Copy AI draft to clipboard"
                                                                >
                                                                    <ClipboardDocumentIcon className="h-4 w-4" /> Copy AI Reply
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="flex gap-4 items-start">
                                                        <div className="flex-1 space-y-3">
                                                            <div>
                                                                <label className="mb-1 block text-xs font-bold text-slate-700">Reply to submitter</label>
                                                                <textarea
                                                                    placeholder="Write the consultant reply before resolving..."
                                                                    className="w-full text-xs p-2 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500"
                                                                    rows={4}
                                                                    value={ticketReplyDrafts[t.id!] || ''}
                                                                    onChange={e => setTicketReplyDrafts(previous => ({ ...previous, [t.id!]: e.target.value }))}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="mb-1 block text-xs font-bold text-slate-500">Internal notes</label>
                                                                <textarea
                                                                    placeholder="Internal notes stay with the management team..."
                                                                    className="w-full text-xs p-2 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500"
                                                                    rows={2}
                                                                    defaultValue={t.resolutionDetails || ''}
                                                                    key={t.id}
                                                                    onBlur={(e) => handleTicketResolution(t.id!, e.target.value).catch(() => showToast('Failed saving resolution.', 'error'))}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="w-48">
                                                            <button
                                                                disabled={ticketResolving === t.id || !(ticketReplyDrafts[t.id!] || '').trim()}
                                                                className="w-full py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-lg uppercase tracking-wider hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                                onClick={() => void handleResolveTicket(t.id!)}
                                                            >
                                                                {ticketResolving === t.id ? 'Resolving...' : 'Resolve & Send Reply'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
        
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        )}
    </section>;
};

export default SupportQueuePanel;
